import { ReactElement, useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Lock } from 'lucide-react';
import { Toggle } from '@/components/Toggle';
import Select from '@/components/Select';
import { Input } from '@/components/Input';
import { apiGetRequest, apiPostRequest, apiPutRequest } from '@/utils/apiClient';
import { fetchCalendarOauthReadiness, redirectToCalendarReauth } from '@/utils/calendarOauthReadiness';
import CalendarReauthConfirmModal from '@/features/scheduledReceipt/CalendarReauthConfirmModal';
import { useUser } from '@/hooks/UserContext';
import { useNextDeliveryDateTime } from '@/hooks/useNextDeliveryDateTime';
import { getWeekday } from '@/utils/time';
import { convert24To12, convert12To24, TIME_FORMAT_REGEX } from '@/utils/deliveryTime';
import { buildPlansUrlWithTracking } from '@/utils/openPlansWithTracking';
import Modal from '@/components/Modal';
import { Accordion } from '@/components/Accordion';
import {
    IndividualCalendarItem,
    createIndividualCalendarOptions,
    getDefaultIndividualCalendarOption,
    getBaseColorOptionsByProvider,
    applyColorOrderWithDividers,
    getDefaultColorOption,
    ColorOption,
} from '@/features/analysis/seach/util/Search';
import { LocalStorageKeys } from '@/constants/localStorage';
import { getLocalStorage } from '@/utils/localStrage';
import { buildLanguageTagFromUser } from '@/utils/locale';

interface ReportDeliverySettingsData {
    enabled: boolean;
    frequency: 'WEEKLY' | 'MONTHLY';
    localTime: string; // HH:mm format
    recipientEmail?: string;
    calendarId?: number;
    color?: { value: string; label: string };
    keyword?: string;
}

/** リマインダー設定（ReminderSettings）と同じ丸数字スタイル */
const reportSectionTitle = (step: 1 | 2, label: string): ReactElement => (
    <div className="flex items-center gap-3 min-w-0">
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-500 text-white text-xs font-bold shrink-0">
            {step}
        </div>
        <span className="text-sm font-medium text-gray-900">{label}</span>
    </div>
);

const ReportDeliverySettings = (): ReactElement => {
    const { userInfo } = useUser();
    const { t, i18n } = useTranslation('scheduledReceipt');

    const isPaidPlan = userInfo?.isPaid === true;
    const languageTag = buildLanguageTagFromUser(userInfo, i18n.language);

    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState<boolean>(false);

    const [enabled, setEnabled] = useState<boolean>(false);
    const [frequency, setFrequency] = useState<'WEEKLY' | 'MONTHLY'>('WEEKLY');
    const [localTime, setLocalTime] = useState<string>('20:00');
    const [recipientEmail, setRecipientEmail] = useState<string>('');
    const [reportCalendarId, setReportCalendarId] = useState<string>('');
    const [reportColor, setReportColor] = useState<ColorOption>(getDefaultColorOption(languageTag));
    const [reportKeyword, setReportKeyword] = useState<string>('');

    const [calendarList, setCalendarList] = useState<IndividualCalendarItem[]>([]);
    const [colorOptionsView, setColorOptionsView] = useState<ColorOption[]>([]);

    // am/pm表記用の状態（内部では24時間形式で保持）
    const [hour, setHour] = useState<number>(8); // 1-12
    const [isAm, setIsAm] = useState<boolean>(false); // true: am, false: pm
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isOAuthReadinessChecking, setIsOAuthReadinessChecking] = useState<boolean>(false);
    const [calendarReauthModalOpen, setCalendarReauthModalOpen] = useState<boolean>(false);
    const [pendingReauthorizePath, setPendingReauthorizePath] = useState<string | null>(null);
    const [isTestSending, setIsTestSending] = useState<boolean>(false);
    const [testSendShowSent, setTestSendShowSent] = useState<boolean>(false);
    const testSendFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [error, setError] = useState<string>('');
    const [testSendError, setTestSendError] = useState<string>('');
    /** null: 未取得。0 でテスト送信ボタンを無効化し上限メッセージを表示 */
    const [testSendRemaining, setTestSendRemaining] = useState<number | null>(null);
    const [success, setSuccess] = useState<string>('');
    const [showEnabledMessage, setShowEnabledMessage] = useState<boolean>(false);
    const [showDisabledMessage, setShowDisabledMessage] = useState<boolean>(false);

    /** API からの初期同期では true にしない（表示直後に save が走らないようにする） */
    const shouldPersistEnabledRef = useRef<boolean>(false);

    useEffect(() => {
        return () => {
            if (testSendFeedbackTimerRef.current) {
                clearTimeout(testSendFeedbackTimerRef.current);
            }
        };
    }, []);

    const [prevEnabled, setPrevEnabled] = useState<boolean>(false);
    const [prevFrequency, setPrevFrequency] = useState<'WEEKLY' | 'MONTHLY'>('WEEKLY');
    const [prevLocalTime, setPrevLocalTime] = useState<string>('20:00');
    const [prevRecipientEmail, setPrevRecipientEmail] = useState<string>('');
    const [prevReportCalendarId, setPrevReportCalendarId] = useState<string>('');
    const [prevReportColor, setPrevReportColor] = useState<ColorOption>(getDefaultColorOption(languageTag));
    const [prevReportKeyword, setPrevReportKeyword] = useState<string>('');

    const timezone = userInfo?.timeZone || 'UTC';

    const localizeColorOption = useCallback(
        (option?: ColorOption | null): ColorOption => {
            if (!option) {
                const base = getDefaultColorOption(languageTag);
                return { ...base, label: t('report.color.none') };
            }
            if (option.isDivider) {
                return option;
            }
            if (option.value === '') {
                return {
                    ...option,
                    label: t('report.color.none'),
                    originalLabel: option.originalLabel ?? option.label,
                };
            }
            if (option.value === 'all') {
                return {
                    ...option,
                    label: t('report.color.all'),
                    originalLabel: option.originalLabel ?? option.label,
                };
            }
            return {
                ...option,
                originalLabel: option.originalLabel ?? option.label,
            };
        },
        [t, languageTag],
    );

    const refreshColorOptions = useCallback(
        async (calendarId: string) => {
            const selected = calendarList?.find((c) => String(c.id) === calendarId);
            const providerName = selected?.providerName || '';
            const base = await getBaseColorOptionsByProvider(providerName, languageTag);
            const userIdValue = userInfo?.userId || '';
            const key = `${LocalStorageKeys.COLOR_ORDER}:${calendarId}`;
            let order: string[] = [];
            const saved = await getLocalStorage(userIdValue, key);
            if (saved) {
                try {
                    order = JSON.parse(saved);
                } catch {
                    order = [];
                }
            }
            const ordered = applyColorOrderWithDividers(base, order).map(localizeColorOption);
            setColorOptionsView(ordered);
        },
        [calendarList, localizeColorOption, userInfo?.userId, languageTag],
    );

    const handleToggleChange = async (next: boolean) => {
        if (!isPaidPlan) {
            setIsUpgradeModalOpen(true);
            return;
        }
        if (!next) {
            setTestSendError('');
        }
        if (next) {
            setIsOAuthReadinessChecking(true);
            setError('');
            setTestSendError('');
            try {
                const calId = reportCalendarId?.trim() ? reportCalendarId : undefined;
                const data = await fetchCalendarOauthReadiness(calId);
                if (!data.ready && data.reauthorizePath) {
                    setIsOAuthReadinessChecking(false);
                    setPendingReauthorizePath(data.reauthorizePath);
                    setCalendarReauthModalOpen(true);
                    return;
                }
            } catch (err) {
                setError(
                    err instanceof Error && err.message === 'CALENDAR_OAUTH_READINESS_NOT_FOUND'
                        ? t('report.oauthCheckEndpointNotFound')
                        : t('report.oauthCheckError'),
                );
                setIsOAuthReadinessChecking(false);
                return;
            }
            setIsOAuthReadinessChecking(false);
        }
        shouldPersistEnabledRef.current = true;
        setEnabled(next);
    };

    useEffect(() => {
        if (!isPaidPlan) {
            setIsLoading(false);
            return;
        }

        const fetchSettings = async () => {
            setIsLoading(true);
            setError('');
            setTestSendError('');
            try {
                const response = await apiGetRequest('/api/delivery/report-settings');
                if (!response.ok) {
                    if (response.status === 404) {
                        setEnabled(false);
                        setFrequency('WEEKLY');
                        const defaultTime = '20:00';
                        setLocalTime(defaultTime);
                        const { hour: defaultHour, isAm: defaultIsAm } = convert24To12(defaultTime);
                        setHour(defaultHour);
                        setIsAm(defaultIsAm);
                        setRecipientEmail(userInfo?.email || '');
                        setReportKeyword('');
                        return;
                    }
                    throw new Error('Failed to fetch settings');
                }
                const data: ReportDeliverySettingsData = await response.json();
                setEnabled(data.enabled);
                setFrequency(data.frequency);
                const initialTime = data.localTime || '20:00';
                setLocalTime(initialTime);
                const { hour: initialHour, isAm: initialIsAm } = convert24To12(initialTime);
                setHour(initialHour);
                setIsAm(initialIsAm);
                const initialEmail = data.recipientEmail || userInfo?.email || '';
                setRecipientEmail(initialEmail);
                if (data.calendarId != null) {
                    setReportCalendarId(String(data.calendarId));
                }
                if (data.color) {
                    setReportColor(localizeColorOption(data.color as ColorOption));
                }
                if (data.keyword != null) {
                    setReportKeyword(data.keyword);
                }
                setPrevEnabled(data.enabled);
                setPrevFrequency(data.frequency);
                setPrevLocalTime(initialTime);
                setPrevRecipientEmail(initialEmail);
                setPrevReportCalendarId(data.calendarId != null ? String(data.calendarId) : '');
                setPrevReportColor(data.color ? localizeColorOption(data.color as ColorOption) : getDefaultColorOption(languageTag));
                setPrevReportKeyword(data.keyword ?? '');
            } catch (err) {
                console.error('Failed to fetch report delivery settings:', err);
                setError(t('report.error'));
            } finally {
                setIsLoading(false);
            }
        };

        if (userInfo?.userId) {
            void fetchSettings();
        }
    }, [userInfo?.userId, userInfo?.email, isPaidPlan, t, localizeColorOption, languageTag]);

    // カレンダーリスト取得
    useEffect(() => {
        const fetchCalendars = async () => {
            try {
                const response = await apiGetRequest('/api/settings/calendars');
                if (!response.ok) return;
                const data = await response.json();
                const calendars: IndividualCalendarItem[] = data.calendars || [];
                setCalendarList(calendars);
            } catch (err) {
                console.error('Failed to fetch calendars:', err);
            }
        };
        if (userInfo?.userId) {
            void fetchCalendars();
        }
    }, [userInfo?.userId]);

    // カレンダー変更時に色オプションを更新
    useEffect(() => {
        if (reportCalendarId) {
            void refreshColorOptions(reportCalendarId);
        }
    }, [reportCalendarId, refreshColorOptions]);

    // カレンダーリスト読み込み後、デフォルトカレンダーを設定
    useEffect(() => {
        if (calendarList.length > 0 && !reportCalendarId) {
            const defaultOpt = getDefaultIndividualCalendarOption(calendarList);
            setReportCalendarId(defaultOpt.value);
        }
    }, [calendarList, reportCalendarId]);

    // 言語変更時に色オプションのラベルを更新
    useEffect(() => {
        setColorOptionsView((prev) => prev.map((opt) => localizeColorOption(opt)));
        setReportColor((prev) => localizeColorOption(prev));
    }, [localizeColorOption]);

    const frequencyOptions = useMemo(
        () => [
            { value: 'WEEKLY', label: t('report.frequency.weekly') },
            { value: 'MONTHLY', label: t('report.frequency.monthly') },
        ],
        [t],
    );

    const selectedFrequencyOption = useMemo(() => {
        return frequencyOptions.find((opt) => opt.value === frequency) || frequencyOptions[0];
    }, [frequency, frequencyOptions]);

    const calendarOptions = useMemo(
        () => createIndividualCalendarOptions(calendarList, { lockNonDefault: !isPaidPlan }),
        [calendarList, isPaidPlan],
    );

    const defaultCalendarOption = useMemo(
        () => getDefaultIndividualCalendarOption(calendarList),
        [calendarList],
    );

    const defaultColorOption = useMemo(
        () => localizeColorOption(getDefaultColorOption(languageTag)),
        [localizeColorOption, languageTag],
    );

    const selectedCalendarOption = useMemo(() => {
        const match = calendarOptions.find((opt) => opt.value === reportCalendarId);
        if (match) return match;
        if (reportCalendarId) return { value: reportCalendarId, label: '' };
        return defaultCalendarOption;
    }, [calendarOptions, defaultCalendarOption, reportCalendarId]);

    const selectedColorOption = useMemo(() => {
        const match = colorOptionsView.find((opt) => opt.value === reportColor?.value);
        if (match) return match;
        if (reportColor) return reportColor;
        return defaultColorOption;
    }, [colorOptionsView, defaultColorOption, reportColor]);

    const hasChanges = useMemo(
        () =>
            enabled !== prevEnabled ||
            frequency !== prevFrequency ||
            localTime !== prevLocalTime ||
            recipientEmail !== prevRecipientEmail ||
            reportCalendarId !== prevReportCalendarId ||
            reportColor?.value !== prevReportColor?.value ||
            reportKeyword !== prevReportKeyword,
        [
            enabled,
            prevEnabled,
            frequency,
            prevFrequency,
            localTime,
            prevLocalTime,
            recipientEmail,
            prevRecipientEmail,
            reportCalendarId,
            prevReportCalendarId,
            reportColor?.value,
            prevReportColor?.value,
            reportKeyword,
            prevReportKeyword,
        ],
    );

    const nextDeliveryDateTime = useNextDeliveryDateTime({ enabled, frequency, hour, isAm, timezone });

    const refreshTestSendRemaining = useCallback(async () => {
        if (!userInfo?.userId) return;
        try {
            const response = await apiGetRequest('/api/delivery/test-send-count?type=report');
            if (!response.ok) return;
            const data: { remaining?: number } = await response.json();
            if (typeof data.remaining === 'number') {
                setTestSendRemaining(data.remaining);
            }
        } catch {
            /* 取得失敗時は既存の remaining を維持 */
        }
    }, [userInfo?.userId]);

    useEffect(() => {
        if (!isPaidPlan) {
            setTestSendRemaining(null);
            return;
        }
        if (isLoading || !userInfo?.userId) return;
        void refreshTestSendRemaining();
    }, [isPaidPlan, isLoading, userInfo?.userId, refreshTestSendRemaining]);

    const saveSettings = async () => {
        if (!isPaidPlan) return;

        setPrevEnabled(enabled);
        setPrevFrequency(frequency);
        setPrevLocalTime(localTime);
        setPrevRecipientEmail(recipientEmail);
        setPrevReportCalendarId(reportCalendarId);
        setPrevReportColor(reportColor);
        setPrevReportKeyword(reportKeyword);

        setIsSaving(true);
        setError('');
        setSuccess('');
        setTestSendError('');

        try {
            const payload = buildReportPayload();
            const response = await apiPutRequest('/api/delivery/report-settings', payload);

            if (!response.ok) {
                throw new Error('Failed to save settings');
            }

            if (enabled && !prevEnabled) {
                setSuccess('');
                setShowEnabledMessage(true);
                setShowDisabledMessage(false);
                setTimeout(() => setShowEnabledMessage(false), 5000);
            } else if (!enabled && prevEnabled) {
                setSuccess('');
                setShowEnabledMessage(false);
                setShowDisabledMessage(true);
                setTimeout(() => setShowDisabledMessage(false), 5000);
            } else {
                setShowEnabledMessage(false);
                setShowDisabledMessage(false);
                setSuccess(t('report.success'));
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            console.error('Failed to save report delivery settings:', err);
            setEnabled(prevEnabled);
            setFrequency(prevFrequency);
            setLocalTime(prevLocalTime);
            setRecipientEmail(prevRecipientEmail);
            setReportCalendarId(prevReportCalendarId);
            setReportColor(prevReportColor);
            setReportKeyword(prevReportKeyword);
            setError(t('report.errorNotSaved'));
        } finally {
            setIsSaving(false);
        }
    };

    const buildReportPayload = () => {
        if (!TIME_FORMAT_REGEX.test(localTime)) {
            throw new Error(t('report.invalidTimeFormat'));
        }

        const effectiveKeyword = reportColor?.value === 'all' ? '' : reportKeyword;
        return {
            enabled,
            frequency,
            localTime,
            recipientEmail,
            calendarId: reportCalendarId ? Number(reportCalendarId) : undefined,
            color: reportColor?.value ? { value: reportColor.value, label: reportColor.label } : undefined,
            keyword: effectiveKeyword || undefined,
        };
    };

    const sendTestEmail = async () => {
        if (!isPaidPlan) return;

        setIsTestSending(true);
        setTestSendError('');
        setSuccess('');

        try {
            const payload = buildReportPayload();
            const response = await apiPostRequest('/api/delivery/report-settings/test-send', payload);
            if (!response.ok) {
                let message = t('report.sendTestError');
                try {
                    const errBody: { error?: string; code?: string } = await response.json();
                    if (errBody.code === 'TEST_SEND_DAILY_LIMIT') {
                        setTestSendRemaining(0);
                        message = t('report.sendTestLimitReached');
                    } else if (typeof errBody.error === 'string' && errBody.error.length > 0) {
                        message = errBody.error;
                    }
                } catch {
                    /* 本文が JSON でない場合はデフォルトメッセージ */
                }
                throw new Error(message);
            }
            const okBody: { remaining?: number } = await response.json();
            setTestSendError('');
            if (typeof okBody.remaining === 'number') {
                setTestSendRemaining(okBody.remaining);
            } else {
                void refreshTestSendRemaining();
            }
            setTestSendShowSent(true);
            if (testSendFeedbackTimerRef.current) {
                clearTimeout(testSendFeedbackTimerRef.current);
            }
            testSendFeedbackTimerRef.current = setTimeout(() => {
                setTestSendShowSent(false);
                testSendFeedbackTimerRef.current = null;
            }, 2500);
        } catch (err) {
            console.error('Failed to send report test email:', err);
            const msg = err instanceof Error ? err.message : t('report.sendTestError');
            if (msg === t('report.sendTestLimitReached')) {
                setTestSendError('');
            } else {
                setTestSendError(msg);
            }
        } finally {
            setIsTestSending(false);
        }
    };

    useEffect(() => {
        if (!isLoading && isPaidPlan && userInfo?.userId && shouldPersistEnabledRef.current) {
            shouldPersistEnabledRef.current = false;
            const timer = setTimeout(() => {
                void saveSettings();
            }, 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    useEffect(() => {
        if (!isLoading) {
            const newTime = convert12To24(hour, isAm);
            setLocalTime(newTime);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hour, isAm]);

    const closeCalendarReauthModal = () => {
        setCalendarReauthModalOpen(false);
        setPendingReauthorizePath(null);
    };

    const confirmCalendarReauth = () => {
        if (pendingReauthorizePath) {
            redirectToCalendarReauth(pendingReauthorizePath);
        }
    };

    if (isLoading) {
        return (
            <section className="flex flex-col gap-3">
                <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-64 mb-4"></div>
                    <div className="h-10 bg-gray-200 rounded w-24"></div>
                </div>
            </section>
        );
    }

    return (
        <section className="flex flex-col gap-4">
            <CalendarReauthConfirmModal
                isOpen={calendarReauthModalOpen}
                onClose={closeCalendarReauthModal}
                onConfirm={confirmCalendarReauth}
                variant="report"
            />
            {showEnabledMessage && enabled && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    {t('report.enabledMessage')}
                </div>
            )}

            {showDisabledMessage && !enabled && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800">
                    {t('report.disabledMessage')}
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                    {error}
                </div>
            )}

            {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    {success}
                </div>
            )}

            {isSaving && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800">
                    {t('report.saving')}
                </div>
            )}

            <div className="flex items-start gap-3 pb-2 min-w-0">
                <FileText className="w-10 h-10 sm:w-11 sm:h-11 text-gray-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            {!isPaidPlan && <Lock className="w-4 h-4 text-gray-600 shrink-0" />}
                            <h3 className="text-lg font-medium">{t('report.heading')}</h3>
                        </div>
                        <Toggle
                            checked={enabled}
                            onChange={(next) => void handleToggleChange(next)}
                            isDisabled={isOAuthReadinessChecking}
                            className="scale-75 origin-left"
                        />
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{t('report.description')}</p>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:gap-8 items-stretch gap-6 min-w-0">
                <div className="flex flex-col gap-4 w-full max-w-xl lg:w-96 lg:max-w-none lg:shrink-0 min-w-0">
                    <div className="relative z-10">
                        <Accordion
                            items={[
                            {
                                title: reportSectionTitle(1, t('report.deliverySettings')),
                                content: (
                                    <div className="flex flex-col gap-6 py-2">
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm font-medium">{t('report.frequency.label')}</label>
                                            <Select
                                                options={frequencyOptions}
                                                defaultValue={selectedFrequencyOption}
                                                onValueChange={(option) => setFrequency(option.value as 'WEEKLY' | 'MONTHLY')}
                                                disabled={!isPaidPlan || !enabled}
                                            />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm font-medium">{t('report.time.label')}</label>
                                            <p className="text-xs text-gray-500">{t('report.time.description', { timezone })}</p>
                                            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                                <div
                                                    className={`flex rounded-lg overflow-hidden border-2 shrink-0 ${
                                                        !isPaidPlan || !enabled ? 'border-gray-300' : 'border-lightest-gray'
                                                    }`}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsAm(true)}
                                                        disabled={!isPaidPlan || !enabled}
                                                        className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                                                            !isPaidPlan || !enabled
                                                                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                                                : isAm
                                                                  ? 'bg-gray-400 text-white'
                                                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {t('report.time.am')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsAm(false)}
                                                        disabled={!isPaidPlan || !enabled}
                                                        className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors border-l-2 ${
                                                            !isPaidPlan || !enabled
                                                                ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300'
                                                                : !isAm
                                                                  ? 'bg-gray-400 text-white border-lightest-gray'
                                                                  : 'bg-white text-gray-700 hover:bg-gray-50 border-lightest-gray'
                                                        }`}
                                                    >
                                                        {t('report.time.pm')}
                                                    </button>
                                                </div>
                                                <select
                                                    value={hour}
                                                    onChange={(e) => setHour(Number(e.target.value))}
                                                    className={`border-2 rounded-lg py-2 px-3 text-sm focus:border-kotomilThemaColor focus:outline-none focus:ring-2 focus:ring-kotomilThemaColor/20 ${
                                                        !isPaidPlan || !enabled
                                                            ? 'bg-gray-100 text-gray-500 border-gray-300 cursor-not-allowed'
                                                            : 'border-lightest-gray'
                                                    }`}
                                                    disabled={!isPaidPlan || !enabled}
                                                >
                                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                                                        <option key={h} value={h}>
                                                            {h}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span className="text-sm text-gray-700">{t('report.time.hour')}</span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm font-medium">{t('report.recipient.label')}</label>
                                            <p className="text-xs text-gray-500">{t('report.recipient.description')}</p>
                                            <input
                                                type="text"
                                                value={recipientEmail || userInfo?.email || ''}
                                                readOnly
                                                className={`border-2 rounded-lg py-2 px-3 text-sm w-full cursor-not-allowed focus:border-kotomilThemaColor focus:outline-none focus:ring-2 focus:ring-kotomilThemaColor/20 ${
                                                    !isPaidPlan || !enabled
                                                        ? 'bg-gray-100 text-gray-500 border-gray-300'
                                                        : 'bg-gray-100 border-lightest-gray'
                                                }`}
                                            />
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                title: reportSectionTitle(2, t('report.analysisTarget')),
                                content: (
                                    <div className="flex flex-col gap-6 py-2">
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm font-medium">{t('report.calendar.label')}</label>
                                            <Select
                                                options={calendarOptions}
                                                defaultValue={selectedCalendarOption}
                                                onValueChange={(option) => {
                                                    if (option.isLocked) {
                                                        setIsUpgradeModalOpen(true);
                                                        return;
                                                    }
                                                    setReportCalendarId(option.value);
                                                    setReportColor(defaultColorOption);
                                                }}
                                                disabled={!isPaidPlan || !enabled}
                                                className="w-full"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm font-medium">{t('report.color.label')}</label>
                                            <p className="text-xs text-gray-500">{t('report.color.description')}</p>
                                            <Select
                                                options={colorOptionsView}
                                                defaultValue={selectedColorOption}
                                                onValueChange={(option) => {
                                                    if ((option as ColorOption).isDivider) return;
                                                    setReportColor(localizeColorOption(option as ColorOption));
                                                }}
                                                disabled={!isPaidPlan || !enabled}
                                                className="w-full"
                                                dropdownClassName="min-w-0 max-w-[min(100vw-2rem,22rem)] sm:min-w-[300px] sm:max-w-none"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm font-medium">{t('report.keyword.label')}</label>
                                            <p className="text-xs text-gray-500">{t('report.keyword.description')}</p>
                                            <Input
                                                className="border-2 border-lightest-gray rounded-lg focus-visible:border-kotomilThemaColor focus-visible:ring-2 focus-visible:ring-kotomilThemaColor/20"
                                                placeholder={
                                                    reportColor?.value === 'all'
                                                        ? t('report.keyword.disabled')
                                                        : t('report.keyword.placeholder')
                                                }
                                                error={false}
                                                helperText=""
                                                showClearButton={reportColor?.value !== 'all'}
                                                value={reportKeyword}
                                                disabled={!isPaidPlan || !enabled || reportColor?.value === 'all'}
                                                onChange={(e) => setReportKeyword(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                ),
                            },
                            ]}
                            allowMultiple={true}
                            defaultOpenIndices={[1]}
                            className="[&>div]:border-b [&>div]:border-gray-200 [&>div:last-child]:border-b-0"
                            titleClassName="p-4 flex items-center justify-between hover:bg-gray-50/80 rounded-t-lg transition-colors"
                            contentClassName="px-4 pb-4"
                        />
                    </div>

                    <div className="relative z-0">
                        <button
                            type="button"
                            onClick={() => void saveSettings()}
                            disabled={!isPaidPlan || isSaving || !hasChanges || !enabled}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border-2 w-full sm:w-32 max-w-xs ${
                                !isPaidPlan || isSaving || !hasChanges || !enabled
                                    ? 'bg-gray-200 border-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-kotomilThemaColor hover:bg-opacity-90 text-black border-kotomilThemaColor'
                            }`}
                        >
                            {isSaving ? t('report.saving') : t('report.updateButton')}
                        </button>
                    </div>
                </div>

                {/* 右側：次回配信日時・プレビュー・テスト送信（有効時のみ表示） */}
                {enabled && (
                    <div className="w-full lg:w-96 lg:flex-shrink-0 flex flex-col gap-4 bg-gray-100 p-4 sm:p-6 lg:p-8 rounded-xl lg:rounded-none min-w-0">
                        <div className="rounded-lg p-4 flex flex-col w-full bg-white min-w-0">
                            <h4 className="text-sm font-medium text-gray-700 mb-3 pb-3 border-b border-gray-300">
                                {t('report.nextDelivery.label')}
                            </h4>
                            {nextDeliveryDateTime ? (
                                <div className="space-y-2">
                                    <div className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                                        {i18n.language?.startsWith('ja')
                                            ? nextDeliveryDateTime.format('YYYY年MM月DD日')
                                            : nextDeliveryDateTime.format('MMMM DD, YYYY')}
                                        <span className="block sm:inline sm:ml-2 text-sm sm:text-base font-normal text-gray-700 mt-1 sm:mt-0">
                                            ({getWeekday(nextDeliveryDateTime.format('YYYY-MM-DD'), i18n.language || 'en')})
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {i18n.language?.startsWith('ja')
                                            ? `${isAm ? t('report.time.am') : t('report.time.pm')}${hour}${t('report.time.hour')}`
                                            : `${hour} ${isAm ? t('report.time.am') : t('report.time.pm')}`}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500">{t('report.nextDelivery.disabled')}</div>
                            )}
                        </div>
                        {isPaidPlan && (
                            <>
                                {/* テスト送信ボタン */}
                                <div className="flex flex-col gap-2 mt-2">
                                    <div className="flex justify-center items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => void sendTestEmail()}
                                            disabled={
                                                !isPaidPlan ||
                                                isTestSending ||
                                                testSendShowSent ||
                                                testSendRemaining === null ||
                                                testSendRemaining <= 0
                                            }
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border-2 w-full max-w-xs sm:w-48 flex items-center justify-center gap-2 ${
                                                !isPaidPlan ||
                                                isTestSending ||
                                                testSendShowSent ||
                                                testSendRemaining === null ||
                                                testSendRemaining <= 0
                                                    ? testSendShowSent
                                                        ? 'bg-green-50 border-green-500 text-green-800 cursor-default'
                                                        : 'bg-gray-200 border-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-white hover:bg-gray-50 text-gray-800 border-kotomilThemaColor shadow-sm'
                                            }`}
                                        >
                                            <span>
                                                {isTestSending
                                                    ? t('report.sendTestSending')
                                                    : testSendShowSent
                                                      ? t('report.sendTestSentButton')
                                                      : t('report.sendTestButton')}
                                            </span>
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 text-center px-2">
                                        {t('report.sendTestTooltip')}
                                    </p>
                                    {testSendRemaining !== null && testSendRemaining <= 0 ? (
                                        <p
                                            className="text-sm text-amber-900 text-center px-2 mt-1"
                                            role="status"
                                        >
                                            {t('report.sendTestLimitReached')}
                                        </p>
                                    ) : null}
                                    {testSendError ? (
                                        <p
                                            className="text-sm text-red-700 text-center px-2 mt-1"
                                            role="alert"
                                        >
                                            {testSendError}
                                        </p>
                                    ) : null}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <Modal
                isOpen={isUpgradeModalOpen}
                onClose={() => setIsUpgradeModalOpen(false)}
                contentClassName="w-full max-w-md p-6"
            >
                <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-gray-600" />
                    {t('report.heading')}
                </h4>
                <div className="text-sm text-gray-700 mb-4 space-y-2 text-center">
                    <p>{t('report.lockedDescriptionLine1')}</p>
                    <p>{t('report.lockedDescriptionLine2')}</p>
                </div>
                <div className="flex flex-wrap justify-center gap-3 mt-4">
                    <button
                        type="button"
                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                        onClick={() => setIsUpgradeModalOpen(false)}
                    >
                        {t('report.cancel')}
                    </button>
                    <a
                        href={buildPlansUrlWithTracking({ source: 'report_delivery_modal' })}
                        className="px-4 py-2 text-sm font-medium text-black bg-kotomilThemaColor rounded-lg hover:bg-opacity-90"
                        onClick={() => setIsUpgradeModalOpen(false)}
                    >
                        {t('report.viewPlans')}
                    </a>
                </div>
            </Modal>
        </section>
    );
};

export default ReportDeliverySettings;
